import { getPendingImage } from "view/components/RichTextEditor/adapters/pendingImages";
import { getScaleFromSrc } from "./getScaleFromSrc";

/**
 * Single source of truth for "how wide should this <img> render".
 *
 * Scale has always been URL metadata (`?scale=` / legacy `&scale=`), which
 * only works while the src is a real http(s) URL. The deferred upload flow
 * (`StorageServiceImageAdapter`) embeds `URL.createObjectURL(blob)` instead,
 * and a `blob:` URL cannot carry a query string — the browser resolves the
 * object by exact key, so `fetch("blob:…?scale=50")` would miss. Reading
 * scale straight off the src therefore always yielded `null` for a pending
 * image, which is why resize silently did nothing until the form was saved
 * (crop was unaffected: it rewrites the blob's pixels, not the URL).
 *
 * For `blob:` srcs the pending-images registry already holds the scale that
 * `EditImageModal` picked, and it lives exactly as long as the blob does —
 * so read it from there and keep the URL parsing for everything else.
 */
export const getImageScale = (src: string): number | null => {
  if (!src) {
    return null;
  }
  if (src.startsWith("blob:")) {
    return getPendingImage(src)?.scale ?? null;
  }
  const raw = getScaleFromSrc(src);
  if (raw == null) {
    return null;
  }
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
};
